'use strict';

import $ from 'jquery';
import _ from 'underscore';
import mediator from '../../js/mediator';
import config from '../../js/config';
import utilities from '../../js/utilities';
import { globalErrorHandler } from '../../utilities/errorHandling';
import CartAPI from '../../services/CartAPI';
import userService from '../../services/CartUserService';
import { EnableHybrisIntegration } from '../../utilities/featureFlags';
import { EnableArrowReel } from '../../utilities/featureFlags';

/**
 * Cart product - As returned from the CartAPI
 *
 * @typedef {Object} CartEntry
 * @property {string} vendorItemNo
 * @property {string} sellerId
 * @property {string} catalogSourcePartId
 * @property {string} catalogSourceCode
 * @property {string} catalogPartNo
 * @property {string} catalogSmallImageURL
 * @property {string} catalogManufacturerSeo
 * @property {string} catalogPartSeo
 * @property {string} catalogSourceId
 * @property {string} catalogDateCode
 * @property {string} partNo
 * @property {string} partCategoryNo
 * @property {string} partCategoryName
 * @property {string} mpn
 * @property {string} manufacturer
 * @property {string} partDescription
 * @property {number} minimumOrderQuantity
 * @property {number} quantity
 * @property {number} unitPrice
 * @property {number} itemTotal
 * @property {number} availableQuantity
 * @property {number} orderIncrement
 * @property {?number} dateCode
 * @property {?number} lotCode
 * @property {number} pedigree
 * @property {string} itemLocation
 * @property {string} itemLocationCodeAsName
 * @property {string} countryOfOrigin
 * @property {boolean} rohsCompliant
 * @property {boolean} leadFree
 * @property {?number} deliveryDate
 * @property {number} shipmentDate
 * @property {boolean} ncnr
 * @property {string} warranty
 * @property {string} smallImage
 * @property {boolean} servicesAvailable
 * @property {object} tieredPricingView
 * @property {?*} saleType
 * @property {?number} originalPrice
 * @property {boolean} customerSpecificPrice
 * @property {string} unitOfMeasure
 * @property {string} eccn
 * @property {boolean} backOrderItem
 * @property {boolean} shipAndDebit
 * @property {string} ipn
 * @property {boolean} factoryStock
 * @property {boolean} candC
 * @property {boolean} onSale
 * @property {string} sellerType
 */

/**
 * The AddedProduct object that is passed around when something is added to the cart
 *
 * @typedef {Object} AddedProduct
 * @property {boolean} [hasAttributes] - The total calculated price for the quantity added
 * @property {string} [itemTotal] - The total calculated price for the quantity added
 * @property {number} quantity - The quantity that was added
 * @property {BuyingOption} model - The buying option model json
 */

class Cart {
  constructor() {
    this.initialize();
  }

  initialize() {
    mediator.on(config.events.cart.add, this.onAddToCart);
    mediator.on(config.events.cart.addMultiple, this.onAddMultipleToCart, this);
    mediator.on(config.events.cart.adding, this.onProductsAddingToCart, this);
  }

  getInitialCartInfo() {
    userService
      .getUserInfo(true)
      .then(() => this.load());
  }

  /**
   * Get the currently stored cart details
   * @deprecated Now use getCurrentCartState
   */
  get() {
    let defer = $.Deferred();
    CartAPI.restoreAllCartDataFromStorage();
    if (CartAPI.cartState) {
      defer.resolve(CartAPI.cartState);
    } else {
      defer.reject();
    }
    return defer.promise();
  }

  /**
   * Get the latest state of the cart
   * @return {Promise<string|null>}
   */
  getCurrentCartState() {
    CartAPI.restoreAllCartDataFromStorage();
    return Promise.resolve(CartAPI.cartState);
  }

  // Load the cart details from the API
  load(cartReponse = null) {
    return CartAPI.syncAllCartData(cartReponse);
  }

  /**
   * Add a product to the cart.
   *
   * @param {AddedProduct[]} addedProducts
   * @returns {Promise<cartContents>}
   */
  addProduct(addedProducts) {
    return CartAPI.addProductToCart(addedProducts);
  }

  /**
   * Remove all parts for the given products from the cart, when they are in the same region.
   *
   * @param {AddedProduct[]} productsToRemove
   * @returns {Promise<null>}
   */
  removeProducts(productsToRemove) {
    // Get a list of all the part ids & regions we are adding to the cart
    let parts = _.chain(productsToRemove)
      .uniq()
      .value();

    return this.getCurrentCartState()
      .then(cart => {
        if (!cart) return null;

        // Get a list of all the vendorItem parts for any part matching the partIds in the cart
        let vendorItems;
        if (EnableHybrisIntegration.isEnabled()) {
          vendorItems = _.chain(parts)
            .reduce((items, part) => items.concat(_.where(cart.entries, { partNo: part.model.partId, catalogSourceCode: part.model.sourceCode })), [])
            .uniq()
            .value();
        } else {
          vendorItems = _.chain(parts)
            .reduce((items, part) => items.concat(_.where(cart.entries, { catalogPartNo: part.model.partId, catalogSourceCode: part.model.sourceCode })), [])
            .uniq()
            .value();
        }

        if (!vendorItems.length) return null;

        return CartAPI.removeFromCart(vendorItems)
          .then(() => null);
      });
  }

  validateHybrisProducts(addedElements) {
    // Get a list of all the part ids & regions we are adding to the cart
    let parts = _.chain(addedElements)
      .uniq()
      .value();

    return CartAPI.getContents(false, true)
      .then(cart => {
        if (!cart) return null;

        // Get a list of all the vendorItem parts for any part matching the partIds in the cart
        let vendorItems = _.chain(parts)
          .reduce((items, part) => items.concat(_.where(cart.entries, { partNo: part.model.partId, catalogSourceCode: part.model.sourceCode })), [])
          .uniq()
          .value();

        if (!vendorItems.length) return null;

        return vendorItems;
      });
  }

  /**
   * A request has come in to add this product to the cart (This will only ever deal with one product).
   *
   * This verifies the quantity and either continues with the add process or triggers a quantity restriction event.
   *
   * @param {AddedProduct[]} addedProducts
   */
  onAddToCart(addedProducts) {
    // Redirect to cart to buy more if the product is already in the cart
    if (addedProducts[0].model.isInCart) {
      document.location = config.urls.cart.cart;
      return;
    }

    let total = utilities.calculateLineItemPrice(
      utilities.parseQuantity(addedProducts[0].quantity),
      addedProducts[0].model
    );

    // Verify the quantity
    if (!isNaN(total)) {
      addedProducts[0].itemTotal = total;
      mediator.trigger(config.events.cart.adding, addedProducts);
    } else {
      mediator.trigger(config.events.cart.quantityRestriction, addedProducts);
    }
  }

  /**
   * Add multiple products to the cart.
   *
   * @private
   * @param {AddedProduct[]} addedProducts
   */
  onAddMultipleToCart(addedProducts) {
    if (EnableHybrisIntegration.isEnabled() && EnableArrowReel.isEnabled()) {
      this.validateHybrisProducts(addedProducts)
        .then((response) => {
          const responseExcludingBundleParts = response ? response.filter(c => !c.bundlePriceTotal) : [];
          if (response === null || !responseExcludingBundleParts.length) {
            this.addProduct(addedProducts)
              .then(this.onAddSuccess)
              .catch(this.onAddFail);
          } else {
            let updateElements = [];
            let deleteElements = [];
            let kitProduct = [];
            response.forEach(cartElement => {
              let cartEntry = addedProducts.filter((entry) => {
                let entryPartId = entry.model.sourcePartId;
                if (entryPartId === cartElement.catalogPartNo && !cartElement.bundlePriceTotal) {
                  kitProduct = response.filter((product) => entryPartId === cartElement.catalogPartNo && product.bundlePriceTotal);
                  return entry;
                }
              });
              if (cartEntry.length === 0 && !cartElement.bundlePriceTotal) {
                deleteElements.push(cartElement);
              }
              if (cartEntry && cartEntry[0]) {
                let args = {};
                args.quantity = cartEntry[0].quantity;
                args.ipn = cartElement.ipn;
                args.vendorItemNo = cartElement.vendorItemNo;
                args.product = cartEntry[0];
                args.catalogCustomReel = false;
                args.entryNumber = cartElement.entryNumber;
                updateElements.push(args);
              }
            });
            let promise = [];
            updateElements.forEach(element => {
              promise.push(CartAPI.updateEntries(element, kitProduct.length ? element.entryNumber : element.product.model.sourcePartId));
            });
            let lastEntry = this.findNewProducts(response, addedProducts);
            if (lastEntry.length > 0) {
              if (deleteElements.length > 0) {
                return CartAPI.removeElementFromCart(deleteElements[0])
                  .then(() => {
                    return this.addProduct(lastEntry)
                      .then((addedProductResponse) => {
                        utilities.sessionStorageSave('cart', addedProductResponse);
                        return Promise.all(promise)
                          .then(() => {
                            this.handleResponse(addedProductResponse, addedProducts);
                          }).catch(this.onAddFail);
                      });
                  });
              } else {
                return this.addProduct(lastEntry)
                  .then((addedProductResponse) => {
                    utilities.sessionStorageSave('cart', addedProductResponse);
                    return Promise.all(promise)
                      .then(() => {
                        this.handleResponse(addedProductResponse, addedProducts);
                      }).catch(this.onAddFail);
                  });
              }
            } else {
              return Promise.all(promise)
                .then((response) => {
                  if (deleteElements.length > 0) {
                    return CartAPI.removeElementFromCart(deleteElements[0])
                      .then(() => {
                        let lastElement = response[response.length - 1];
                        this.handleResponse(lastElement, addedProducts);
                      });
                  } else {
                    let lastElement = response[response.length - 1];
                    this.handleResponse(lastElement, addedProducts);
                  }
                }).catch(this.onAddFail);
            }
          }
        });
    } else {
      this.removeProducts(addedProducts)
        .then(() => this.addProduct(addedProducts))
        .then(this.onAddSuccess)
        .catch(this.onAddFail);
    }
  }

  /**
   * When a product is in the process of being added to the cart
   * @private
   * @param {AddedProduct[]} addedProducts
   */
  onProductsAddingToCart(addedProducts) {
    this.addProduct(addedProducts)
      .then(this.onAddSuccess)
      .catch(this.onAddFail);
  }

  findNewProducts(cartProducts, addedProducts) {
    let cartIds = [];
    let newIds = [];
    let newEntries = [];
    cartProducts.forEach(cartElement => {
      cartIds.push(cartElement.catalogPartNo);
    });
    addedProducts.forEach(added => {
      let sourcePart = added.model.sourcePartId;
      newIds.push(sourcePart);
    });
    let addElements = _.difference(newIds, cartIds);
    addElements.forEach(id => {
      let newEntry = addedProducts.filter((entry) => {
        return entry.model.sourcePartId === id;
      });
      if (newEntry.length > 0) {
        newEntries.push(newEntry[0]);
      }
    });
    return newEntries;
  }

  handleResponse(addedProductResponse, addedProducts) {
    addedProductResponse.addedProducts = addedProducts;
    utilities.sessionStorageSave('cart', addedProductResponse);
    this.onAddSuccess(addedProductResponse);
  }

  /**
   * @private
   * @param cart
   */
  onAddSuccess(cart) {
    mediator.trigger(config.events.cart.added, cart);
  }

  /**
   * @private
   * @param error Generic error interface
   */
  onAddFail(error) {
    globalErrorHandler(error);
    mediator.trigger(config.events.cart.failedToAdd, error.addedProducts);
  }

  // Checks to see if the product id is in the cart provided
  isProductInCart(productId, cart) {
    if (!productId || !cart) {
      return false;
    }

    return !!_.find(cart.entries, entry => this.getCartCatalogSourcePartId(entry) === productId);
  }

  isCutomReelCartEntry(cartItem) {
    let isCustomReel = false;
    if (EnableArrowReel.isEnabled()) {
      if (EnableHybrisIntegration.isEnabled()) {
        isCustomReel = cartItem.reelDetails && cartItem.reelDetails.mpn === config.hybris.arrowCustomReel;
      } else {
        isCustomReel = cartItem.catalogCustomReel;
      }
    }
    return isCustomReel;
  }


  // Infers correct sourcePartId if Arrow Reel feature enabled
  getCartCatalogSourcePartId(cartItem) {
    let cartItemSourcePartId = cartItem.catalogSourcePartId;
    if (this.isCutomReelCartEntry(cartItem)) {
      cartItemSourcePartId = cartItemSourcePartId + '-AR';
    }
    return cartItemSourcePartId;
  }

  //Infers correct Part Number if Hybris intergration enabled
  getPartNumber(cartEntry) {
    let partNumber = cartEntry.catalogPartNo;
    if (EnableHybrisIntegration.isEnabled()) {
      partNumber = cartEntry.partNo;
    }
    return partNumber;
  }

  /**
   * Download a quote PDF.
   *
   * @param {string} fullName
   * @param {string} emailAddress
   * @param {Object[]} products
   * @param {string} products[].sourceCode
   * @param {string} products[].sourcePartId
   * @param {number} products[].quantity
   */
  downloadQuote(fullName, emailAddress, products) {
    let $iFrame = $('#downloadQuote');

    if (!$iFrame.length) {
      $iFrame = $('<iframe>', {
        id: 'downloadQuote',
        frameborder: 0,
        scrolling: 'no',
        css: { visibility: 'hidden', display: 'none' }
      }).appendTo('body');
    }

    $iFrame.attr('src', CartAPI.getQuoteDownloadLink(fullName, emailAddress, products));
  }

  /**
   * Backorder
   *
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} emailAddress
   * @param {Object} product
   * @param {string} product.sourceCode
   * @param {string} product.sourcePartId
   * @param {number} product.quantity
   * @param {string} product.partNumber
   * @param {string} product.manufacturer
   * @param {string} product.type
   * @param {string} inventoryRegion
   * @param {Object} salesForceInfo
   * @param {number} [quantityAddedToCart = 0]
   * @returns {Promise.<T>}
   */
  backorder(firstName, lastName, emailAddress, product, productDetails, inventoryRegion, salesForceInfo, quantityAddedToCart = 0) {
    return CartAPI.backorder(
      firstName,
      lastName,
      emailAddress,
      product.quantity,
      product.partNumber,
      productDetails.partSeo,
      product.manufacturer,
      productDetails.manufacturerSeo,
      product.type,
      inventoryRegion,
      salesForceInfo,
      quantityAddedToCart
    );
  }
}

export default new Cart();



// WEBPACK FOOTER //
// ./assets/components/Cart/Cart.js